import { useSearchParams } from "react-router";
import { z } from "zod";
import { useCallback } from "react";

export const CatalogSortSchema = z.enum(["name", "updatedAt", "createdAt"]);
export type CatalogSort = z.infer<typeof CatalogSortSchema>;

export const CatalogSortOrderSchema = z.enum(["asc", "desc"]);
export type CatalogSortOrder = z.infer<typeof CatalogSortOrderSchema>;

const FilterSchema = z.object({
    search: z.string().optional(),
    sortBy: z
        .string()
        .transform(val => (CatalogSortSchema.safeParse(val).success ? CatalogSortSchema.parse(val) : undefined))
        .optional(),
    sortOrder: z
        .string()
        .transform(val => (CatalogSortOrderSchema.safeParse(val).success ? CatalogSortOrderSchema.parse(val) : undefined))
        .optional(),
    labelIds: z
        .string()
        .transform(val =>
            val
                .split(",")
                .map(Number)
                .filter(n => !isNaN(n)),
        )
        .optional(),
    limit: z
        .string()
        .transform(val => Number(val))
        .optional(),
    offset: z
        .string()
        .transform(val => Number(val))
        .optional(),
});

export type CatalogFilterParams = z.infer<typeof FilterSchema>;

export function parseCatalogFilterParams(searchParams: URLSearchParams): CatalogFilterParams {
    const filterKeys = Object.keys(FilterSchema.shape);
    const searchParamKeyVal = filterKeys.map(key => [key, searchParams.get(key)]);
    const searchParamObjectEntries = searchParamKeyVal.filter(([_, value]) => value !== null);
    const searchParamObject = Object.fromEntries(searchParamObjectEntries);
    return FilterSchema.parse(searchParamObject);
}

export function useCatalogListFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = parseCatalogFilterParams(searchParams);

    const setFilters = useCallback(
        (newFilters: Partial<CatalogFilterParams>) => {
            setSearchParams(prev => {
                const params = new URLSearchParams(prev);

                Object.entries(newFilters).forEach(([key, value]) => {
                    if (value === undefined) {
                        params.delete(key);
                    } else if (Array.isArray(value)) {
                        value.length ? params.set(key, value.join(",")) : params.delete(key);
                    } else {
                        params.set(key, String(value));
                    }
                });

                return params;
            });
        },
        [setSearchParams],
    );

    const clearFilters = () => {
        setSearchParams(new URLSearchParams());
    };

    const getActiveFilterCount = () => {
        let count = 0;
        if (filters.labelIds && filters.labelIds.length > 0) count += 1;
        return count;
    };

    return { filters, setFilters, clearFilters, getActiveFilterCount };
}
